import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { DateInput } from "./DateInput";
import { Label } from "./ui/label";
import { MoneyInput } from "./MoneyInput";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const TYPES = [["vr", "Vale-refeição"], ["va", "Vale-alimentação"], ["vt", "Vale-transporte"], ["saude", "Plano de saúde"], ["odonto", "Odontológico"], ["outro", "Outro"]];
const EMPTY = { type: "vr", label: "", value: "", discount: "0", start: new Date().toISOString().slice(0, 10), reason: "" };

const EmployeePicker = ({ emps, picked, setPicked }) => {
  const [q, setQ] = useState("");
  const [dep, setDep] = useState("all");
  const deps = useMemo(() => [...new Set(emps.map((e) => e.department).filter(Boolean))].sort(), [emps]);
  const vis = emps.filter((e) => (dep === "all" || e.department === dep) && (!q || e.name.toLowerCase().includes(q.toLowerCase())));
  const allOn = vis.length > 0 && vis.every((e) => picked.has(e.id));
  const toggle = (ids, on) => setPicked((p) => { const n = new Set(p); ids.forEach((i) => (on ? n.add(i) : n.delete(i))); return n; });
  return (
    <div className="space-y-2">
      <div className="flex gap-2"><Input data-testid="bulk-benefit-search" placeholder="Buscar funcionário" value={q} onChange={(e) => setQ(e.target.value)} />
        <Select value={dep} onValueChange={setDep}><SelectTrigger className="w-48" data-testid="bulk-benefit-dept"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos os departamentos</SelectItem>{deps.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
      <label className="flex items-center gap-2 text-xs text-slate-600 px-1"><Checkbox data-testid="bulk-benefit-select-all" checked={allOn} onCheckedChange={(v) => toggle(vis.map((e) => e.id), !!v)} />Selecionar todos visíveis ({vis.length})</label>
      <div className="max-h-56 overflow-y-auto border rounded-lg divide-y" data-testid="bulk-benefit-list">
        {vis.map((e) => (
          <label key={e.id} className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
            <Checkbox data-testid={`bulk-benefit-emp-${e.id}`} checked={picked.has(e.id)} onCheckedChange={(v) => toggle([e.id], !!v)} />
            <span className="flex-1">{e.name}</span><span className="text-xs text-slate-500">{e.department}</span></label>))}
      </div>
    </div>);
};

export const BulkBenefitDialog = ({ open, onOpenChange, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [emps, setEmps] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(EMPTY); setPicked(new Set());
    api.get("/employees").then((r) => setEmps(r.data.filter((e) => e.status !== "desligado")));
  }, [open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (f.value === "" || (f.type === "outro" && !f.label.trim()) || !picked.size) { toast.error("Preencha benefício, valor e selecione funcionários."); return; }
    setSaving(true);
    try {
      const r = await api.post("/benefits/bulk", { ...f, value: Number(f.value), discount: Number(f.discount || 0), employee_ids: [...picked] });
      toast.success(`Demanda #${r.data.protocol} enviada ao DP · ${r.data.included} inclusão(ões), ${r.data.updated} atualização(ões).`);
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="bulk-benefit-dialog">
        <DialogHeader><DialogTitle>Incluir benefício para vários funcionários</DialogTitle>
          <DialogDescription>O benefício entra no cadastro de cada um (quem já tem é atualizado) e uma única demanda é enviada ao DP.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5"><Label>Benefício</Label><Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger data-testid="bulk-benefit-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`bulk-benefit-type-${k}`}>{l}</SelectItem>)}</SelectContent></Select></div>
          {f.type === "outro" && <div className="space-y-1.5"><Label>Nome do benefício</Label><Input data-testid="bulk-benefit-label" value={f.label} onChange={set("label")} /></div>}
          <div className="space-y-1.5"><Label>Valor (R$)</Label><MoneyInput data-testid="bulk-benefit-value" value={f.value} onChange={set("value")} /></div>
          <div className="space-y-1.5"><Label>Desconto (R$)</Label><MoneyInput data-testid="bulk-benefit-discount" value={f.discount} onChange={set("discount")} /></div>
          <div className="space-y-1.5"><Label>Vigência</Label><DateInput testid="bulk-benefit-start" value={f.start} onChange={(v) => setF({ ...f, start: v })} /><p className="text-[11px] text-slate-500" data-testid="bulk-benefit-start-hint">Para quem foi admitido depois desta data, vale a data de admissão.</p></div>
          <div className="space-y-1.5 col-span-2 sm:col-span-1"><Label>Motivo (opcional)</Label><Input data-testid="bulk-benefit-reason" value={f.reason} onChange={set("reason")} /></div>
        </div>
        <EmployeePicker emps={emps} picked={picked} setPicked={setPicked} />
        <DialogFooter><span className="text-sm text-slate-500 mr-auto self-center" data-testid="bulk-benefit-count">{picked.size} selecionado(s)</span>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-benefit-cancel">Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="bulk-benefit-save">{saving ? "Enviando..." : "Incluir e enviar ao DP"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>);
};
